import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCreditCard,
  lucideLandmark,
  lucideTriangleAlert,
  lucideWallet,
  lucideX,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LoggerService } from '../../services/logger.service';

import { StripeService } from '../../services/stripe.service';
import { firstValueFrom } from 'rxjs';
import { Router, ActivatedRoute, Params, RouterLink } from '@angular/router';
import { CompanyService, Company } from '../../services/company.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { formatRate } from '../../utils/currency.utils';

/** Schenkwerk's standard share of a sale; a shop's own rate can be lower (referrals). */
const STANDARD_FEE_RATE = 0.05;

interface StripeComponent extends Node {
  setOnEvent?: (callback: (event: any) => void) => void;
  setOnLoaderStart?: (callback: (event: { elementTagName: string }) => void) => void;
  setOnNotificationsChange?: (
    callback: (response: { total: number; actionRequired: number }) => void,
  ) => void;
  destroy?: () => void;
}

interface FinancialTab {
  label: string;
  icon: string;
  id: string;
}

@Component({
  selector: 'app-financial-dashboard',
  standalone: true,
  imports: [
    RouterLink,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmSpinnerImports,
    HlmTabsImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideCreditCard,
      lucideLandmark,
      lucideTriangleAlert,
      lucideWallet,
      lucideX,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './financial-dashboard.component.html',
})
export class FinancialDashboardComponent implements OnInit, OnDestroy {
  loading = false;
  error: string | null = null;
  /** Stripe's banner reports this; until then the mount point stays collapsed. */
  hasStripeNotifications = false;
  activeTab = 0;
  company: Company | null = null;
  private stripeStarted = false;

  /** Loaded, but no Stripe account yet: payments still have to be set up. */
  get needsStripeSetup(): boolean {
    return !!this.company && !this.company.stripe_account_id;
  }
  private destroy$ = new Subject<void>();
  private stripeComponents: Record<string, StripeComponent> = {};

  private allTabs: FinancialTab[] = [
    { label: 'Auszahlungen', icon: 'lucideWallet', id: 'payouts' },
    { label: 'Kontostand', icon: 'lucideLandmark', id: 'balances' },
    { label: 'Zahlungen', icon: 'lucideCreditCard', id: 'payments' },
  ];

  /** A network gets paid out like any shop: it settles with its members itself. */
  get tabs(): FinancialTab[] {
    return this.allTabs;
  }

  constructor(
    private stripeService: StripeService,
    private companyService: CompanyService,
    private router: Router,
    private route: ActivatedRoute,
    private logger: LoggerService,
    private zone: NgZone,
  ) {}

  readonly formatRate = formatRate;
  readonly standardFeeRate = STANDARD_FEE_RATE;

  /** A plain shop's fee for new sales; networks have their own terms, members sell nothing. */
  get shopFeeRate(): number | null {
    const company = this.company;
    if (!company || company.is_member || company.is_group_admin || company.fee_rate == null) {
      return null;
    }
    return company.fee_rate;
  }

  get activeTabId(): string {
    return this.tabs[this.activeTab]?.id ?? '';
  }

  ngOnInit() {
    this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company: Company) => {
      this.company = company;
      // Stripe's components need the company's own Stripe account: a shop that hasn't
      // set up payments yet gets a hint instead of a failing account session.
      if (!this.stripeStarted && company.stripe_account_id) {
        this.stripeStarted = true;
        this.initializeStripeDashboard();
      }
    });

    this.route.queryParams.subscribe((params: Params) => {
      const tabParam = params['tab'];
      // The bookkeeping documents moved to Einstellungen → Belege; keep old links working.
      if (tabParam === 'accounting') {
        this.router.navigate(['/settings'], {
          queryParams: { tab: 'accounting-statement' },
          replaceUrl: true,
        });
        return;
      }
      if (tabParam) {
        const tabIndex = this.tabs.findIndex((tab) => tab.id === tabParam);
        if (tabIndex !== -1) {
          this.activeTab = tabIndex;
        }
      }
    });
  }

  onTabChange(index: number) {
    this.activeTab = index;
    this.router.navigate([], {
      queryParams: { tab: this.tabs[index].id },
      queryParamsHandling: 'merge',
    });
  }

  private async initializeStripeDashboard() {
    try {
      this.loading = true;
      this.error = null;

      // Create all components using the new method that handles session expiration
      this.stripeComponents = {
        payouts: await this.stripeService.createStripeComponent('payouts'),
        payments: await this.stripeService.createStripeComponent('payments'),
        balances: await this.stripeService.createStripeComponent('balances'),
        'payment-details': await this.stripeService.createStripeComponent('payment-details'),
        notificationBanner: await this.stripeService.createStripeComponent('notification-banner'),
      };

      // Mount notification banner. It reports how many notifications it has, so
      // the wrapper can stay collapsed while there is nothing to show — an empty
      // banner element still takes a slot in the page's `space-y` rhythm.
      const bannerContainer = document.getElementById('stripe-notification-mount');
      const banner = this.stripeComponents['notificationBanner'];
      if (bannerContainer && !bannerContainer.hasChildNodes()) {
        if (typeof banner.setOnNotificationsChange === 'function') {
          banner.setOnNotificationsChange(({ total }) => {
            this.zone.run(() => {
              this.hasStripeNotifications = total > 0;
            });
          });
        }
        bannerContainer.appendChild(banner);
      }

      // Mount all components with loading states
      const mountPoints = [
        { id: 'stripe-balances-mount', key: 'balances', label: 'Balance Overview' },
        { id: 'stripe-payments-mount', key: 'payments', label: 'Recent Payments' },
        { id: 'stripe-payouts-mount', key: 'payouts', label: 'Payouts' },
        { id: 'stripe-payment-details-mount', key: 'payment-details', label: 'Payment Details' },
      ];

      mountPoints.forEach(({ id, key, label }) => {
        const container = document.getElementById(id);
        const component = this.stripeComponents[key];

        if (container && !container.hasChildNodes() && component) {
          // Set up loading handlers
          if (typeof component.setOnLoaderStart === 'function') {
            component.setOnLoaderStart((event) => {
              const loader = document.getElementById(`${id}-loader`);
              if (loader) loader.style.display = 'none';
            });
          }

          // Mount component
          container.appendChild(component);

          // Set up event handler
          if (component.setOnEvent) {
            component.setOnEvent((event: any) => {
              // this.logger.log(`${key} event:`, event);
            });
          }
        }
      });
    } catch (err: any) {
      this.error = err.message || 'An error occurred while setting up the Stripe dashboard';
      this.logger.error('Stripe dashboard setup error:', err);
    } finally {
      this.loading = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    Object.values(this.stripeComponents).forEach((component) => {
      if (component && typeof component.destroy === 'function') {
        component.destroy();
      }
    });
  }
}
